import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { formatSAR } from "@/i18n";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowDownLeft } from "lucide-react";

export default function RequestsInbox({ requests, onResolved }) {
  const { t, lang } = useApp();
  const [busy, setBusy] = useState(null);
  const incoming = requests?.incoming || [];

  if (!incoming.length) return null;

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/requests/${id}/${action}`);
      toast.success(action === "approve" ? t.toastPaid : t.toastDeclined);
      onResolved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="pending-requests-section" className="mb-6">
      <h3 className="text-sm font-bold text-muted-foreground mb-3 uppercase tracking-wide">{t.pendingRequests}</h3>
      <div className="space-y-2.5">
        <AnimatePresence>
          {incoming.map((r) => (
            <motion.div
              key={r.request_id}
              data-testid="incoming-request-item"
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0 }}
              className="p-4 rounded-2xl bg-card border border-primary/20"
            >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-primary/15 text-primary grid place-items-center shrink-0">
                  <ArrowDownLeft className="w-5 h-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">
                    {r.requester_name} <span className="text-muted-foreground font-normal">{t.requestedFromYou}</span>
                  </p>
                  {r.note && <p className="text-xs text-muted-foreground truncate">{r.note}</p>}
                </div>
                <div className="text-end shrink-0">
                  <p className="font-bold tabular-nums text-primary text-sm">{formatSAR(r.amount, lang)}</p>
                  <p className="text-[11px] text-muted-foreground">{t.currency}</p>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  data-testid="request-pay-btn"
                  onClick={() => act(r.request_id, "approve")}
                  disabled={!!busy}
                  className="flex-1 h-10 rounded-xl font-bold"
                >
                  {busy === r.request_id + "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : t.pay}
                </Button>
                <Button
                  data-testid="request-decline-btn"
                  onClick={() => act(r.request_id, "decline")}
                  disabled={!!busy}
                  variant="outline"
                  className="flex-1 h-10 rounded-xl font-bold"
                >
                  {busy === r.request_id + "decline" ? <Loader2 className="w-4 h-4 animate-spin" /> : t.decline}
                </Button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
